import React, { useState, useEffect, useCallback } from "react";
import { Modal, ModalFooter, ModalHeader, ModalTitle } from '@common/components/modal';
import { I18n } from "react-redux-i18n";
import cx from "classnames";

import Tree from "@common/components/Tree";
import { Search } from "@common/components/Search";
import { Flex } from "@common/components/Flex";
import { Ellipsis } from "@common/components/Ellipsis";
import { getTestAttributes } from "@common/utils/tests";
import { HttpErrorDto } from "@common/dto/http";
import { FolderListDto } from "@common/dto/folder/list-dto";
import { PedigreeGetDto } from "@common/dto/pedigree/get-dto";
import { TreeGetIdArgs } from "@common/components/Tree/tree.types";
import { Button } from "@common/components/Button";
import { PedigreeListDto } from "@common/dto/pedigree/list-dto";

import { Loading } from "../loading";

import "./pedigree-selector.styles.scss";

type PedigreeSelectorProps = {
  withSearch?: boolean;
  getFolders: () => Promise<FolderListDto[]>;
  getPedigrees: (params?: {
    folderId?: number;
    size?: number;
    startAt?: number;
  }) => Promise<PedigreeListDto[]>;
  onSubmit: (data: {
    folder?: FolderListDto;
    pedigree: PedigreeGetDto;
  }) => void;
  onClose: () => void;
  getId: {
    title: string;
    searchIcon: string;
    search: string;
    cancelBtn: string;
    submitBtn: string;
    field: (pedigree: PedigreeGetDto, rowIndex: number) => string;
    folder: ({ parentIndex, ownIndex }: TreeGetIdArgs) => string;
  };
  showErrorMessage: (message?: string) => void;
  individualSearch: (params: { query: string; maxSuggestRows: number }) => Promise<{
    value: string;
    id: string;
    parentId: string;
    img: string;
  }[]>;
};

export const PedigreeSelector = (props: PedigreeSelectorProps) => {
  const {
    onSubmit,
    onClose,
    getFolders,
    getPedigrees,
    individualSearch,
    withSearch,
    getId,
    showErrorMessage,
  } = props;

  const [selectedFolder, setSelectedFolder] = useState<FolderListDto>();
  const [selectedPedigree, setSelectedPedigree] = useState<PedigreeGetDto>();
  const [folders, setFolders] = useState<FolderListDto[]>([]);
  const [pedigrees, setPedigrees] = useState<PedigreeListDto[]>([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pedigreesLoading, setPedigreesLoading] = React.useState(false);
  const [pedigreesLoadingMore, setPedigreesLoadingMore] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(true);
  const pageSize = 100;
  const fieldsContainerRef = React.useRef<HTMLDivElement>(null);
  const startAtRef = React.useRef(0);

  useEffect(() => {
    handleGetFolders();
  }, []);

  const handleGetFolders = async () => {
    try {
      const folders = await getFolders();
      setFolders(folders);

      return folders;
    } catch (err) {
      console.error(err);

      const errorMessage =
          (err as HttpErrorDto)?.entity || I18n.t("Failed to get folders");
      showErrorMessage(errorMessage);
    }
  };

  const getSearchedPedigrees = React.useCallback(async ({ searchKeyword }: { searchKeyword: string }) => {
    try {
      setPedigreesLoading(true);
      const result = await individualSearch({ query: searchKeyword, maxSuggestRows: 100 });
      const normalizedPedigrees = result.map((item) => ({ id: item.id, parentId: item.parentId, name: item.value, img: item.img }));
      setPedigrees(normalizedPedigrees);
    } catch (err) {
      console.error(err);

      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t("Failed to load pedigrees");
      props.showErrorMessage(errorMessage);
    } finally {
      setPedigreesLoading(false);
    };
  }, [individualSearch]);


  const getPedigreesData = React.useCallback(async (queries?: {
    folderId?: number;
  }, append: boolean = false) => {
    try {
      if (append) {
        setPedigreesLoadingMore(true);
      } else {
        setPedigreesLoading(true);
        startAtRef.current = 0;
        setHasMore(true);
      }

      const currentStartAt = startAtRef.current;
      const result = await getPedigrees({
        ...queries,
        size: pageSize,
        startAt: currentStartAt,
      });

      if (append) {
        setPedigrees((prev) => [...prev, ...result]);
      } else {
        setPedigrees(result);
      }

      // Check if there are more pedigrees to load
      setHasMore(result.length === pageSize);
      startAtRef.current = currentStartAt + result.length;

      return result;
    } catch (err) {
      console.error(err);

      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t("Failed to load pedigrees");
      props.showErrorMessage(errorMessage);

      // Reset loading state on error
      if (append) {
        setPedigreesLoadingMore(false);
      } else {
        setPedigreesLoading(false);
      }
    } finally {
      if (append) {
        setPedigreesLoadingMore(false);
      } else {
        setPedigreesLoading(false);
      }
    }
  }, [getPedigrees, pageSize, showErrorMessage]);

  const handleFolderChange = useCallback(
      (id: FolderListDto["id"], folder: FolderListDto) => {
        if (id === selectedFolder?.id) {
          return;
        }

        setSearchKeyword("");
        setSelectedFolder(folder);
        setPedigrees([]);
        getPedigreesData({
          folderId: folder?.id,
        }, false);
      },
      [selectedFolder?.id, getPedigreesData]
  );

  const loadMorePedigrees = useCallback(() => {
    if (!hasMore || pedigreesLoadingMore || pedigreesLoading) {
      return;
    }

    if (selectedFolder?.id) {
      getPedigreesData({
        folderId: selectedFolder.id,
      }, true);
    }
  }, [hasMore, pedigreesLoadingMore, pedigreesLoading, selectedFolder?.id, getPedigreesData]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    // Load more when user scrolls within 100px of the bottom
    if (scrollHeight - scrollTop - clientHeight < 100) {
      loadMorePedigrees();
    }
  }, [loadMorePedigrees]);

  const handleFieldChange = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        e.persist();
        const field = pedigrees.find((f) => Number(f.id) === Number(e.currentTarget.dataset.id));
        setSelectedPedigree(field);
      },
      [pedigrees]
  );

  const handleSubmit = React.useCallback(() => {
    if(selectedPedigree) {
      setSubmitting(true);
      onSubmit({
        pedigree: selectedPedigree,
        folder: selectedFolder,
      });
      setSubmitting(false);
    }
  }, [onSubmit, selectedPedigree?.id, selectedFolder?.id]);

  return (
      <Modal
          show
          backdrop="static"
          bsClass="bootstrap modal"
          className="PedigreeSelector"
          dialogClassName="PedigreeSelector__dialog"
          animation={false}
          onEscapeKeyDown={onClose}
          onHide={onClose}
      >
        <ModalHeader onHide={onClose} closeButton className="PedigreeSelector__dialog__header">
          <ModalTitle
              {...getTestAttributes({
                id:
                    getId.title ||
                    getTestAttributes("PedigreeSelector", "modal", "title").id,
              })}
          >
            {I18n.t("Pedigree Chooser")}
          </ModalTitle>
        </ModalHeader>
        <div
            className={cx("PedigreeSelector__content", {
              "PedigreeSelector__content--with-search": withSearch,
            })}
        >
          {withSearch && (
              <Search
                  iconProps={getTestAttributes({ id: getId.searchIcon })}
                  {...getTestAttributes({ id: getId.search })}
                  withLiveSearch
                  value={searchKeyword}
                  placeholder={I18n.t("Search for Pedigree")}
                  className="PedigreeSelector__pedigree-search"
                  onSearch={getSearchedPedigrees}
                  autoFocus
              />
          )}
          <Flex
              width="100%"
              className="PedigreeSelector__content__folders-fields"
          >
            <Tree
                getId={{
                  folder: getId.folder,
                }}
                className="PedigreeSelector__content__folders"
                items={folders}
                value={selectedFolder ? selectedFolder.id : undefined}
                onChange={handleFolderChange}
            />
            <div
                ref={fieldsContainerRef}
                className="PedigreeSelector__content__fields"
                onScroll={handleScroll}
                style={{ position: "relative" }}
            >
              {pedigreesLoading ? (
                  <Flex
                      justify="center"
                      alignItems="center"
                      className="PedigreeSelector__loading__bg"
                  >
                    <Loading size={40} className="PedigreeSelector__loading" />
                  </Flex>
              ) : (
                  <>
                    {pedigrees?.map((field, rowIndex) => (
                        <button
                            title={field.name}
                            key={field.id}
                            data-id={field.id}
                            {...getTestAttributes({
                              id: getId.field
                                  ? getId.field(field, rowIndex)
                                  : `field-id-${field.id}`,
                            })}
                            className={cx("PedigreeSelector__content__fields__item", {
                              "PedigreeSelector__content__fields__item-selected":
                                  field.id === (selectedPedigree && selectedPedigree.id),
                            })}
                            onClick={handleFieldChange}
                        >
                          <img
                              src="gif/pedigree.gif"
                              alt={field.name}
                              className="PedigreeSelector__content__fields__item-icon"
                          />
                          <Ellipsis text={field.name} width="calc(100% - 20px)" />
                        </button>
                    ))}
                    {pedigreesLoadingMore && (
                        <Flex
                            justify="center"
                            alignItems="center"
                            className="PedigreeSelector__loading__bg"
                        >
                          <Loading size={40} className="PedigreeSelector__loading" />
                        </Flex>
                    )}
                  </>
              )}
            </div>
          </Flex>
        </div>
        <ModalFooter>
          <Button
              disabled={submitting}
              {...getTestAttributes({
                id: getId.cancelBtn || "btn-field-select-cancel",
              })}
              onClick={onClose}
          >
            {I18n.t("Cancel")}
          </Button>
          <Button
              {...getTestAttributes({
                id: getId.submitBtn || "btn-field-select-add",
              })}
              disabled={!selectedPedigree || submitting}
              className="btn-primary"
              onClick={handleSubmit}
          >
            {I18n.t("Add")}
          </Button>
        </ModalFooter>
      </Modal>
  );
};